/**
 * Componente de Inicio de Sesión (Login).
 * Maneja la autenticación de usuarios, el segundo factor de autenticación (2FA) 
 * y las solicitudes de acceso para nuevos usuarios. Incluye un fondo animado interactivo.
 */

import React, { useEffect, useRef, useState } from "react";
import "../../assets/css/Login.css";
import logo from "../../assets/img/LogoGeo.png";
import icon from "../../assets/img/login.png";
import { Apiurl } from "../../shared/services/apirest";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import parasite1 from "../../assets/img/parasite_bg_1.png";
import parasite2 from "../../assets/img/parasite_bg_2.png";
import parasite3 from "../../assets/img/parasite_bg_3.png";

function Login() {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const timeoutsRef = useRef([]);
  const navigate = useNavigate();

  const [plani, setPlani] = useState({ email: "", password: "", otp_code: "" });
  const [error, setError] = useState(false);
  const [errorMSG, setErrorMSG] = useState("");
  const [info, setInfo] = useState(false);
  const [infoMSG, setInfoMSG] = useState("");
  const [loading, setLoading] = useState(false);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState({
    full_name: "",
    email: "",
    reason: "",
    estimated_time: ""
  });
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);

  /**
   * Maneja la visualización del mensaje informativo sobre la recuperación de contraseña.
   */
  const handleForgotPassword = (e) => {
    e.preventDefault();
    setInfo(true);
    setInfoMSG("Por favor comuníquese con el administrador para restablecer su contraseña.");
    setError(false);

    // Ocultar automáticamente después de 5 segundos para mantener la interfaz limpia
    setTimeout(() => {
      setInfo(false);
      setInfoMSG("");
    }, 5000);
  };

  /**
   * Actualiza el estado del formulario de login al cambiar los inputs.
   */
  const operadorChange = (e) => {
    setPlani((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  /**
   * Ejecuta el proceso de autenticación contra el backend.
   */
  const operadorboton = async (e) => {
    e.preventDefault();
    setError(false);
    setErrorMSG("");
    setInfo(false); // Limpiar mensajes informativos previos

    if (!plani.email?.trim() || !plani.password?.trim()) {
      setError(true);
      setErrorMSG("Por favor ingrese usuario y contraseña.");
      return;
    }

    if (twoFactorRequired && !plani.otp_code?.trim()) {
      setError(true);
      setErrorMSG("Por favor ingrese el código de verificación (2FA).");
      return;
    }

    setLoading(true);
    const url = `${Apiurl}users/login/`;

    try {
      const response = await axios.post(url, plani, { timeout: 10000 });

      // Verificar si se requiere Segundo Factor de Autenticación
      if (response.data?.["2fa_required"]) {
        setTwoFactorRequired(true);
        setLoading(false);
        return;
      }

      const data = response.data;
      if (!data?.access) {
        throw new Error("No se pudo extraer el token de acceso de la respuesta.");
      }

      const role = data.user?.role;
      localStorage.setItem("token", data.access);
      localStorage.setItem("refresh", data.refresh);
      localStorage.setItem("role", role || "");
      localStorage.setItem("userData", JSON.stringify(data.user));

      // Redirección según el rol del usuario
      if (role === "admin" || role === "admin_lab" || role === "admin_investigacion") {
        navigate("/DashboardAdmin");
      } else if (role === "laboratorio") {
        navigate("/GestionLab");
      } else if (role === "investigador") {
        navigate("/DashboardInvestigador");
      } else if (role === "visualizador") {
        navigate("/DashboardMain");
      } else {
        setError(true);
        setErrorMSG(`Acceso denegado: El rol '${role}' no tiene un tablero asignado.`);
      }
    } catch (err) {
      let msg = "Error inesperado. Intente nuevamente más tarde.";
      if (err.response) {
        msg =
          err.response.data?.detail ||
          err.response.data?.message ||
          err.response.data?.error ||
          "Credenciales incorrectas o acceso denegado.";
      } else if (err.request) {
        msg = "No se pudo conectar con el servidor. Verifique su conexión.";
      }
      setError(true);
      setErrorMSG(msg);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Actualiza el estado de la solicitud de acceso.
   */
  const handleRequestChange = (e) => {
    setRequestForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  /**
   * Envía la solicitud de acceso al administrador.
   */
  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setRequestLoading(true);
    setError(false);
    const url = `${Apiurl}users/access-requests/create/`;

    try {
      await axios.post(url, requestForm);
      setRequestSuccess(true);
      setRequestForm({ full_name: "", email: "", reason: "", estimated_time: "" });
      setTimeout(() => {
        setShowRequestModal(false);
        setRequestSuccess(false);
      }, 3000);
    } catch (err) {
      setError(true);
      setErrorMSG("No se pudo enviar la solicitud. Intente nuevamente.");
    } finally {
      setRequestLoading(false);
    }
  };

  /**
   * Efecto para inicializar y gestionar el fondo animado (Partículas de parásitos).
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w = (canvas.width = window.innerWidth);
    let h = (canvas.height = window.innerHeight);
    const mouse = { x: undefined, y: undefined, radius: 140 };

    // Pre-carga de imágenes de los parásitos para la animación
    const images = [parasite1, parasite2, parasite3].map(src => {
      const img = new Image();
      img.src = src;
      return img;
    });

    function onMouseMove(e) {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    }
    function onResize() {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      init();
    }

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("resize", onResize);

    class Particle {
      constructor(x, y) {
        this.x = x;
        this.y = y;
        // Tamaño aleatorio para variar la profundidad visual
        this.size = Math.random() * 20 + 25;
        this.vx = (Math.random() - 0.5) * 0.6;
        this.vy = (Math.random() - 0.5) * 0.6;
        this.image = images[Math.floor(Math.random() * images.length)];
        this.angle = Math.random() * 360;
        this.vAngle = (Math.random() - 0.5) * 0.5;
      }
      draw() {
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.angle * Math.PI / 180);
        ctx.globalAlpha = 0.6; // Transparencia sutil
        if (this.image.complete && this.image.naturalWidth > 0) {
          const aspectRatio = this.image.naturalWidth / this.image.naturalHeight;
          const drawWidth = this.size * aspectRatio;
          const drawHeight = this.size;
          ctx.drawImage(this.image, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
        } else if (this.image.complete) {
          ctx.drawImage(this.image, -this.size / 2, -this.size / 2, this.size, this.size);
        }
        ctx.restore();
      }
      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.angle += this.vAngle;

        // Efecto envolvente (Pantalla infinita)
        if (this.x < 0 - this.size) this.x = w + this.size;
        if (this.x > w + this.size) this.x = 0 - this.size;
        if (this.y < 0 - this.size) this.y = h + this.size;
        if (this.y > h + this.size) this.y = 0 - this.size;

        // Interacción con el ratón (Repulsión)
        if (mouse.x && mouse.y) {
          const dx = mouse.x - this.x;
          const dy = mouse.y - this.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < mouse.radius) {
            this.x -= dx / 30;
            this.y -= dy / 30;
          }
        }
      }
    }

    let particles = [];
    function init() {
      particles = [];
      const count = Math.max(20, Math.floor((w * h) / 150000) * 8);
      for (let i = 0; i < count; i++) {
        particles.push(new Particle(Math.random() * w, Math.random() * h));
      }
    }

    function animate() {
      ctx.fillStyle = 'rgba(236, 239, 230, 0.3)'; // Rastro sutil
      ctx.fillRect(0, 0, w, h);

      particles.forEach((p) => {
        p.update();
        p.draw();
      });

      rafRef.current = requestAnimationFrame(animate);
    }

    init();
    animate();

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("resize", onResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      timeoutsRef.current.forEach((id) => clearTimeout(id));
    };
  }, []);

  return (
    <>
      <canvas
        id="bgCanvas"
        ref={canvasRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 0,
          display: "block",
        }}
      />

      <div className="login-wrapper" style={{ zIndex: 2 }}>
        <div className="login-card bordered-login thin-border">
          <div className="header-login">
            <button
              className="home-nav-btn"
              onClick={() => navigate("/")}
              title="Volver al Inicio"
            >
              <i className="bi bi-house-door-fill"></i>
            </button>
            <img src={icon} className="login-icon" alt="Login Icon" />
            <h2 className="brand-title">INICIO DE SESIÓN</h2>
          </div>

          <div className="logo-wrapper elegant-frame">
            <img src={logo} className="logo-img" alt="Logo GEOHCX" />
          </div>

          <form id="loginForm" noValidate onSubmit={operadorboton}>
            <div className="mb-3">
              <input
                name="email"
                type="text"
                className="form-control"
                placeholder="Digite su Usuario"
                required
                autoComplete="username"
                onChange={operadorChange}
                value={plani.email}
              />
            </div>

            <div className="mb-3">
              <input
                name="password"
                type="password"
                className="form-control"
                placeholder="Digite su contraseña"
                required
                autoComplete="current-password"
                onChange={operadorChange}
                value={plani.password}
              />
            </div>

            {twoFactorRequired && (
              <div className="mb-3 animate__animated animate__fadeInDown">
                <input
                  name="otp_code"
                  type="text"
                  className="form-control border-primary"
                  placeholder="Código de Verificación (2FA)"
                  required
                  onChange={operadorChange}
                  value={plani.otp_code}
                  style={{ backgroundColor: '#f0f7ff', fontWeight: 'bold' }}
                />
                <small className="text-primary mt-1 d-block fw-bold">
                  <i className="bi bi-shield-lock-fill me-1"></i>
                  Ingrese el código de su aplicación de seguridad
                </small>
              </div>
            )}

            <div className="options">
              <label className="remember-label">
                <input type="checkbox" />{" "}
                <span className="remember-text">Memorizar usuario</span>
              </label>
              <a className="forgot-link" href="#" onClick={handleForgotPassword}>
                ¿Olvidaste tu contraseña?
              </a>
            </div>

            <button
              id="submitBtn"
              type="submit"
              className="btn-accent"
              disabled={loading}
            >
              {loading ? "Cargando..." : "INICIAR SESIÓN"}
            </button>
            <div className="text-center mt-3">
              <button
                type="button"
                className="btn btn-link text-decoration-none"
                style={{ color: '#D4A373', fontWeight: 'bold' }}
                onClick={() => setShowRequestModal(true)}
              >
                ¿Necesita acceso? Solicite su registro aquí
              </button>
            </div>
          </form>

          {showRequestModal && (
            <div className="modal-overlay">
              <div className="modal-content-request">
                <div className="modal-header-request">
                  <h3>Solicitar Acceso</h3>
                  <button className="close-btn" onClick={() => setShowRequestModal(false)}>&times;</button>
                </div>
                <div className="modal-body-request">
                  {requestSuccess ? (
                    <div className="alert alert-success">
                      ¡Solicitud enviada con éxito! El administrador la revisará pronto.
                    </div>
                  ) : (
                    <form onSubmit={handleRequestSubmit}>
                      <div className="mb-3">
                        <label className="form-label text-white">Nombre Completo</label>
                        <input
                          type="text"
                          name="full_name"
                          className="form-control"
                          required
                          value={requestForm.full_name}
                          onChange={handleRequestChange}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label text-white">Correo Electrónico</label>
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          required
                          value={requestForm.email}
                          onChange={handleRequestChange}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label text-white">Motivo de la solicitud</label>
                        <textarea
                          name="reason"
                          className="form-control"
                          required
                          value={requestForm.reason}
                          onChange={handleRequestChange}
                        ></textarea>
                      </div>
                      <div className="mb-3">
                        <label className="form-label text-white">Tiempo estimado de uso</label>
                        <input
                          type="text"
                          name="estimated_time"
                          className="form-control"
                          placeholder="Ej: 3 meses, Permanente"
                          required
                          value={requestForm.estimated_time}
                          onChange={handleRequestChange}
                        />
                      </div>
                      <button type="submit" className="btn-accent w-100" disabled={requestLoading}>
                        {requestLoading ? "Enviando..." : "Enviar Solicitud"}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div
              className="alert alert-danger mt-3 text-center"
              role="alert"
              style={{
                fontSize: "0.95rem",
                borderRadius: "10px",
                marginTop: "15px",
                marginBottom: "0",
                transition: "opacity 0.5s ease-in-out",
              }}
            >
              {errorMSG}
            </div>
          )}

          {info && (
            <div
              className="alert alert-info animate__animated animate__fadeIn"
              role="alert"
            >
              <i className="bi bi-info-circle-fill me-2"></i>
              {infoMSG}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Login;
